import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { HelpCircle, Search, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerFaq = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState({}); // faq id -> expanded

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/faqs`);
        setFaqs(res.data || []);
      } catch (e) {
        console.error("Failed to load FAQs:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q) ||
        (f.category || "").toLowerCase().includes(q)
    );
  }, [faqs, search]);

  const grouped = useMemo(() => {
    const map = {};
    for (const f of filtered) {
      const cat = f.category || "General";
      (map[cat] = map[cat] || []).push(f);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <HelpCircle size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">FAQ</h1>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-2.5 text-white/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search FAQs…"
          className="pl-9 h-10 bg-[#091c3c] border-[#1a3a6a] text-white placeholder:text-white/40"
        />
      </div>

      {faqs.length === 0 ? (
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardContent className="p-12 text-center text-white/50">
            <HelpCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p>No FAQs available yet</p>
            <p className="text-sm mt-1">Check back soon, or start a support chat for help.</p>
          </CardContent>
        </Card>
      ) : grouped.length === 0 ? (
        <p className="text-white/50 text-sm">No FAQs match “{search}”.</p>
      ) : (
        grouped.map(([category, items]) => (
          <Card key={category} className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
            <CardHeader className="py-3 px-5 border-b border-[#1a3a6a]">
              <CardTitle className="font-['Chivo'] text-base text-[#f4cc1e]">{category}</CardTitle>
            </CardHeader>
            <CardContent className="px-2 py-1">
              {items
                .slice()
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((faq) => {
                  const isOpen = !!open[faq.id];
                  return (
                    <div key={faq.id} className="border-b border-[#1a3a6a] last:border-b-0">
                      <button
                        onClick={() => setOpen((o) => ({ ...o, [faq.id]: !o[faq.id] }))}
                        className="w-full flex items-center justify-between gap-3 text-left px-3 py-3 hover:bg-[#0d2a5a] rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-white">{faq.question}</span>
                        <ChevronDown
                          size={16}
                          className={`text-white/50 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {isOpen && (
                        <p className="text-sm text-white/70 px-3 pb-4 whitespace-pre-wrap">{faq.answer}</p>
                      )}
                    </div>
                  );
                })}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default CustomerFaq;
